import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import useModalBackButton from '../../hooks/useModalBackButton';
import {
  Plus,
  Edit2,
  Trash2,
  Search,
  ChevronDown,
  X,
  Save,
  Sparkles,
  TrendingUp,
  Layers,
  Eye,
  EyeOff,
  Check,
  Calendar,
  Clock,
  User,
  History,
  ArrowDownUp,
  BriefcaseBusiness,
  Users,
  WashingMachine,
  BrushCleaning
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import '../../styles/Management.css';
import * as servicesApi from '../../services/servicesApi';
import DatePicker from '../../components/DatePicker';
import FloatingField from '../../components/FloatingField';
import { ConfirmModal } from '../../components/ConfirmModal';
import IconPickerModal from '../../components/IconPickerModal';
import ServiceActivityModal from '../../components/ServiceActivityModal';
import useToast from '../../hooks/useToast';
import { haptic } from '../../utils/vibration';
import ModalShell from '../../components/ModalShell';
import {
  getServiceIconComponent,
  SERVICE_ICON_OPTIONS
} from '../../utils/serviceIcons';
const DESCRIPTION_MAX_LENGTH = 300;

// Типы услуг для отображения
const SERVICE_TYPE_LABELS = {
  washing: 'Стирка',
  cleaning: 'Химчистка мебели',
  extra_washing: 'Доп. услуги стирка',
  extra_cleaning: 'Доп. услуги химчистка мебели',
  extra_order: 'Доп. услуги (Заказ)'
};

const SERVICE_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'Все типы' },
  { value: 'washing', label: 'Стирка' },
  { value: 'cleaning', label: 'Химчистка мебели' },
  { value: 'extra_washing', label: 'Доп. услуги стирка' },
  { value: 'extra_cleaning', label: 'Доп. услуги химчистка мебели' },
  { value: 'extra_order', label: 'Доп. услуги заказа' }
];

const getServiceTypeLabel = (value, fallback) => SERVICE_TYPE_LABELS[value] || fallback || value;

// Единицы измерения
const UNIT_LABELS = {
  шт: 'шт.',
  кг: 'кг.',
  м2: 'м²'
};

const ORDER_EXTRA_SERVICE_UNIT = 'шт';

const PROCESSING_ROUTE_OPTIONS = [
  { value: 'laundry', label: 'Прачечная' },
  { value: 'cleaning', label: 'Чистка' }
];

const PROCESSING_ROUTE_META = {
  laundry: {
    label: 'Прачечная',
    icon: WashingMachine,
    className: 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
  },
  cleaning: {
    label: 'Чистка',
    icon: BrushCleaning,
    className: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
  }
};

const EXECUTION_CREDIT_OPTIONS = [
  { value: 'auto_item_flow', label: 'Автоматически по потоку предмета' },
  { value: 'washing_laundry', label: 'Прачечная' },
  { value: 'washing_cleaning', label: 'Чистка' },
  { value: 'drying_packing', label: 'Сушка и упаковка' },
  { value: 'cleaning', label: 'Химчистка мебели' },
  { value: 'pickup', label: 'Забор' },
  { value: 'delivery', label: 'Доставка' },
  { value: 'handover', label: 'Выдача' },
  { value: 'none', label: 'Не учитывать как выполненную' },
];

const EXECUTION_CREDIT_LABELS = Object.fromEntries(
  EXECUTION_CREDIT_OPTIONS.map((option) => [option.value, option.label])
);

const getDefaultExecutionCreditStage = (serviceType, systemCode = '') => {
  if (systemCode === 'floor_lift') return 'delivery';
  if (serviceType === 'extra_washing' || serviceType === 'extra_cleaning') return 'auto_item_flow';
  if (serviceType === 'extra_order') return 'none';
  return null;
};

const getExecutionCreditOptions = (serviceType) => (
  serviceType === 'extra_order'
    ? EXECUTION_CREDIT_OPTIONS.filter((option) => option.value !== 'auto_item_flow')
    : EXECUTION_CREDIT_OPTIONS
);

const SERVICE_SORT_OPTIONS = [
  { value: 'name_asc', label: 'А-Я' },
  { value: 'name_desc', label: 'Я-А' },
  { value: 'created_desc', label: 'Сначала новые' },
  { value: 'created_asc', label: 'Сначала старые' }
];

const isExtraServiceType = (value) => String(value || '').startsWith('extra_');
const isOrderExtraServiceType = (value) => String(value || '') === 'extra_order';
const formatServicePrice = (price, unitType = 'rub') => (
  price === null || price === undefined || price === ''
    ? '—'
    : `${price}${unitType === 'percent' ? '%' : ' ₽'}`
);
const formatServiceDate = (value) => (
  value ? new Date(value).toLocaleDateString('ru-RU') : '—'
);

function ProcessingRouteOptionLabel({ value }) {
  const routeMeta = PROCESSING_ROUTE_META[value];
  if (!routeMeta) return null;
  const RouteIcon = routeMeta.icon;
  return (
    <span className="inline-flex items-center gap-2">
      <RouteIcon className="h-4 w-4 shrink-0" />
      <span>{routeMeta.label}</span>
    </span>
  );
}

// Красивый Dropdown как в NewOrder.js
function Dropdown({ value, options, onChange, placeholder, error, disabled = false, className = '', buttonClassName = '' }) {
  const [open, setOpen] = useState(false);
  const [direction, setDirection] = useState('down');
  const ref = useRef();

  React.useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const selected = options.find(opt => opt.value === value);
  const selectedContent = selected?.renderLabel || selected?.label;

  const handleToggle = () => {
    if (disabled) return;
    haptic(open ? 'close' : 'open');
    if (!open) {
      const rect = ref.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const dropdownHeight = Math.min(options.length * 40, 200);
      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        setDirection('up');
      } else {
        setDirection('down');
      }
    }
    setOpen(v => !v);
  };

  return (
    <div className={`relative ${open ? 'z-[90]' : 'z-10'} ${className}`} ref={ref}>
      <button
        type="button"
        onClick={handleToggle}
        disabled={disabled}
        className={`w-full px-3 py-2.5 border rounded-xl flex items-center justify-between focus:outline-none focus:ring-2 transition-colors text-sm ${disabled
          ? 'bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border-gray-200 dark:border-gray-700 cursor-not-allowed'
          : error
            ? 'bg-white dark:bg-gray-900 border-red-300 focus:ring-red-500'
            : value
              ? 'bg-white dark:bg-gray-900 border-blue-300 focus:ring-blue-500'
              : 'bg-white dark:bg-gray-900 border-gray-300 focus:ring-blue-500 dark:border-gray-700 dark:focus:ring-blue-500'
          } ${buttonClassName}`}
      >
        <span className={value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}>
          {selected ? selectedContent : placeholder}
        </span>
        <ChevronDown className={`w-4 h-4 ml-2 text-gray-400 dark:text-gray-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-[100] w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg max-h-56 overflow-y-auto ${direction === 'up' ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
          >
            {options.map(opt => (
              <motion.button
                key={opt.value}
                type="button"
                onClick={() => { haptic('selection'); onChange(opt.value); setOpen(false); }}
                className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm border-b border-gray-100 dark:border-gray-700 last:border-b-0 flex items-center justify-between ${value === opt.value ? 'font-semibold text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/10' : 'text-gray-900 dark:text-gray-100'
                  }`}
                whileHover={{ backgroundColor: '#f9fafb' }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="min-w-0 flex-1">{opt.renderLabel || opt.label}</span>
                {value === opt.value && <Check className="w-4 h-4 text-blue-500" />}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
      {error && <p className="text-red-600 dark:text-red-400 text-xs mt-1">{error}</p>}
    </div>
  );
}

function ManagementSegmentedControl({ value, options, onChange, columns, compact = false }) {
  return (
    <div
      className="grid gap-1 rounded-xl bg-gray-100 p-1 dark:bg-gray-800"
      style={{ gridTemplateColumns: `repeat(${columns || options.length}, minmax(0, 1fr))` }}
    >
      {options.map((option) => {
        const Icon = option.icon;
        const active = value === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => {
              haptic(active ? 'tap' : 'selection');
              onChange(option.value);
            }}
            className={`flex items-center justify-center rounded-lg font-medium transition-all ${
              compact ? 'px-3 py-2 text-[13px]' : 'px-4 py-2.5 text-[13px]'
            } ${
              active
                ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                : 'text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100'
            }`}
          >
            {Icon ? <Icon className={compact ? 'mr-1.5 h-4 w-4' : 'mr-2 h-4 w-4'} /> : null}
            <span className="truncate">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function ServicesListSkeleton({ activeTab = 'services' }) {
  const rows = activeTab === 'services' ? 5 : 4;
  return (
    <div className="space-y-2 animate-pulse animate-slide-up">
      <div className="hidden items-center justify-between rounded-xl bg-gray-50 px-4 py-3 dark:bg-gray-800 sm:flex">
        <div className="h-4 w-48 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-9 w-9 rounded-xl bg-gray-200 dark:bg-gray-700" />
      </div>
      <div className="flex items-center justify-between rounded-xl bg-gray-50 px-3 py-2 dark:bg-gray-800 sm:hidden">
        <div className="h-4 w-24 rounded-full bg-gray-200 dark:bg-gray-700" />
        <div className="h-9 w-9 rounded-xl bg-gray-200 dark:bg-gray-700" />
      </div>
      {Array.from({ length: rows }).map((_, index) => (
        <div key={`${activeTab}-skeleton-${index}`} className="rounded-2xl bg-white p-3 shadow-sm dark:bg-gray-900 sm:p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 shrink-0 rounded-xl bg-gray-200 dark:bg-gray-700" />
            <div className="min-w-0 flex-1 space-y-2">
              <div className="h-4 w-40 rounded-full bg-gray-200 dark:bg-gray-700" />
              <div className="flex gap-2">
                <div className="h-5 w-24 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-5 w-16 rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                <div className="h-3 w-20 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-24 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-14 rounded-full bg-gray-200 dark:bg-gray-700" />
                <div className="h-3 w-16 rounded-full bg-gray-200 dark:bg-gray-700" />
              </div>
            </div>
            <div className="flex gap-2">
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
              <div className="h-8 w-8 rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const ServicesManagement = () => {
  const { toast, showToast, hideToast } = useToast(3200);
  // Состояния
  const [activeTab, setActiveTab] = useState('services'); // services | groups
  const [clientType, setClientType] = useState('b2c'); // 'b2c' | 'b2b'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Данные
  const [services, setServices] = useState([]);
  const [groups, setGroups] = useState([]);
  const [serviceTypes, setServiceTypes] = useState([]);
  const [units, setUnits] = useState([]);

  // Фильтры
  const [selectedType, setSelectedType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [serviceSort, setServiceSort] = useState('name_asc');

  // Модальные окна
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showPriceModal, setShowPriceModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedService, setSelectedService] = useState(null);

  const closeServiceModal = useModalBackButton(showServiceModal, () => setShowServiceModal(false));
  const closeGroupModal = useModalBackButton(showGroupModal, () => setShowGroupModal(false));
  const closePriceModal = useModalBackButton(showPriceModal, () => setShowPriceModal(false));

  // Форма услуги
  const [serviceForm, setServiceForm] = useState({
    name: '',
    service_type: 'washing',
    processing_route: 'laundry',
    execution_credit_stage: null,
    group_id: null,
    unit: 'шт',
    base_price: 0,
    unit_type: 'rub',  // 'rub' или 'percent'
    description: '',
    sort_order: 0,
    client_type: 'b2c',
    icon: null  // ← Иконка
  });

  // Форма группы
  const [groupForm, setGroupForm] = useState({
    name: '',
    service_type: 'washing',
    client_type: 'b2c',
    allow_multiple: true,
    description: '',
    sort_order: 0,
    icon: null  // ← Иконка
  });

  // Модальное окно выбора иконки
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [iconPickerType, setIconPickerType] = useState('service'); // 'service' или 'group'

  // Форма цены
  const [priceForm, setPriceForm] = useState({
    price: '',
    unit_type: 'rub',  // 'rub' или 'percent'
    effective_from: '',  // YYYY-MM-DD
    comment: ''
  });

  // История цен
  const [priceHistory, setPriceHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [cancelingScheduledPrice, setCancelingScheduledPrice] = useState(false);

  const tomorrowDateString = () => new Date(Date.now() + 86400000).toISOString().split('T')[0];
  const getScheduledPriceChange = (history = []) => history.find(item => item.is_scheduled);

  // История услуги (аудит)
  const [showAudit, setShowAudit] = useState(false);
  const [selectedAuditService, setSelectedAuditService] = useState(null);

  const closeAuditModal = useModalBackButton(showAudit, () => {
    setShowAudit(false);
    setSelectedAuditService(null);
  });

  // Модальные окна подтверждений и алертов
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    confirmText: 'Подтвердить',
    cancelText: 'Отмена',
    onConfirm: null,
    servicesList: []
  });

  // Загрузка данных
  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Загружаем услуги и группы с фильтрацией по clientType
      const [servicesData, groupsData, typesData, unitsData] = await Promise.all([
        servicesApi.getServices({ active_only: !showInactive, client_type: clientType }),
        servicesApi.getServiceGroups({ active_only: !showInactive, client_type: clientType }),
        servicesApi.getServiceTypes(),
        servicesApi.getUnits()
      ]);

      setServices(servicesData);
      setGroups(groupsData);
      setServiceTypes(typesData.types || []);
      setUnits(unitsData.units || []);
    } catch (err) {
      setError(err.message);
      showToast(err.message || 'Не удалось загрузить услуги и группы', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showInactive, clientType]); // Только эти зависимости

  // Фильтрация услуг
  const filteredServices = services.filter(service => {
    const matchesType = selectedType === 'all' || service.service_type === selectedType;
    const matchesSearch = service.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const sortedServices = [...filteredServices].sort((left, right) => {
    const leftName = String(left?.name || '');
    const rightName = String(right?.name || '');
    const leftCreated = Date.parse(left?.created_date || '') || 0;
    const rightCreated = Date.parse(right?.created_date || '') || 0;

    switch (serviceSort) {
      case 'name_desc':
        return rightName.localeCompare(leftName, 'ru', { sensitivity: 'base' });
      case 'created_desc':
        return rightCreated - leftCreated || leftName.localeCompare(rightName, 'ru', { sensitivity: 'base' });
      case 'created_asc':
        return leftCreated - rightCreated || leftName.localeCompare(rightName, 'ru', { sensitivity: 'base' });
      case 'name_asc':
      default:
        return leftName.localeCompare(rightName, 'ru', { sensitivity: 'base' });
    }
  });

  // Фильтрация групп
  const filteredGroups = groups.filter(group => {
    const matchesType = selectedType === 'all' || group.service_type === selectedType;
    const matchesSearch = group.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const selectedServiceSupportsPercent = isExtraServiceType(selectedService?.service_type);
  const parsedPriceFormValue = parseFloat(priceForm.price);
  const isPricePercentDisallowed = priceForm.unit_type === 'percent' && !selectedServiceSupportsPercent;
  const isPricePercentOverLimit = priceForm.unit_type === 'percent'
    && Number.isFinite(parsedPriceFormValue)
    && parsedPriceFormValue > 100;
  const isPriceSaveDisabled = !priceForm.effective_from
    || !priceForm.price
    || Number.isNaN(parsedPriceFormValue)
    || parsedPriceFormValue <= 0
    || isPricePercentDisallowed
    || isPricePercentOverLimit;

  const normalizeDuplicateName = (value) => String(value || '').trim().toLowerCase();

  const findServiceDuplicate = () => services.find((service) => (
    normalizeDuplicateName(service.name) === normalizeDuplicateName(serviceForm.name)
    && service.service_type === serviceForm.service_type
    && service.client_type === (serviceForm.client_type || clientType)
    && service.id !== editingItem?.id
  ));

  const findGroupDuplicate = () => groups.find((group) => (
    normalizeDuplicateName(group.name) === normalizeDuplicateName(groupForm.name)
    && group.service_type === groupForm.service_type
    && group.client_type === (groupForm.client_type || clientType)
    && group.id !== editingItem?.id
  ));

  // Обработчики

  const handleOpenServiceModal = (service = null) => {
    haptic('open');
    if (service) {
      setEditingItem(service);
      setServiceForm({
        name: service.name,
        service_type: service.service_type,
        processing_route: service.processing_route || 'laundry',
        execution_credit_stage: service.execution_credit_stage || getDefaultExecutionCreditStage(service.service_type, service.system_code),
        group_id: service.group_id,
        unit: isOrderExtraServiceType(service.service_type) ? ORDER_EXTRA_SERVICE_UNIT : (service.unit || ORDER_EXTRA_SERVICE_UNIT),
        base_price: service.base_price,
        description: service.description || '',
        sort_order: service.sort_order || 0,
        client_type: service.client_type || clientType,
        icon: service.icon || null
      });
    } else {
      setEditingItem(null);
      setServiceForm({
        name: '',
        service_type: 'washing',
        processing_route: 'laundry',
        execution_credit_stage: null,
        group_id: null,
        unit: 'шт',
        base_price: 0,
        description: '',
        sort_order: 0,
        client_type: clientType,
        icon: null
      });
    }
    setShowServiceModal(true);
  };

  const handleOpenGroupModal = (group = null) => {
    haptic('open');
    if (group) {
      setEditingItem(group);
      setGroupForm({
        name: group.name,
        service_type: group.service_type,
        client_type: group.client_type || 'b2c',
        allow_multiple: group.allow_multiple,
        description: group.description || '',
        sort_order: group.sort_order || 0,
        icon: group.icon || null
      });
    } else {
      setEditingItem(null);
      setGroupForm({
        name: '',
        service_type: 'washing',
        client_type: clientType,
        allow_multiple: true,
        description: '',
        sort_order: 0,
        icon: null
      });
    }
    setShowGroupModal(true);
  };

  const handleOpenPriceModal = async (service) => {
    haptic('open');
    setSelectedService(service);
    setPriceForm({
      price: service.current_price ?? service.base_price ?? '',
      unit_type: isExtraServiceType(service.service_type) ? (service.current_unit_type || 'rub') : 'rub',
      effective_from: tomorrowDateString(),
      comment: ''
    });
    setShowHistory(false);

    // Загружаем историю цен при открытии модального окна
    try {
      const priceHistoryData = await servicesApi.getServicePrices(service.id, clientType);
      setPriceHistory(priceHistoryData);
      const scheduledChange = getScheduledPriceChange(priceHistoryData);
      if (scheduledChange) {
        setPriceForm({
          price: scheduledChange.price,
          unit_type: isExtraServiceType(service.service_type) ? (scheduledChange.unit_type || 'rub') : 'rub',
          effective_from: scheduledChange.effective_from
            ? new Date(scheduledChange.effective_from).toISOString().split('T')[0]
            : tomorrowDateString(),
          comment: ''
        });
      }
    } catch (err) {
      console.error('Ошибка загрузки истории цен:', err);
    }

    setShowPriceModal(true);
  };

  const handleOpenAuditModal = async (service) => {
    haptic('open');
    setSelectedAuditService(service);
    setShowAudit(true);
  };

  const handleEditServiceClick = (service) => {
    if (service.system_code && !isExtraServiceType(service.service_type)) {
      showAlertModal(
        'Недоступно',
        'Системную услугу нельзя редактировать через эту кнопку. Для изменения цены используйте кнопку с графиком.',
        'warning'
      );
      return;
    }

    handleOpenServiceModal(service);
  };

  const showConfirmModal = (title, message, onConfirm, type = 'danger', confirmText = 'Подтвердить', servicesList = []) => {
    haptic(type === 'danger' ? 'warning' : type === 'warning' ? 'warning' : 'info');
    setConfirmModal({
      isOpen: true,
      title,
      message,
      type,
      confirmText,
      cancelText: 'Отмена',
      onConfirm,
      servicesList
    });
  };

  const showAlertModal = (title, message, type = 'info') => {
    haptic(type === 'success' ? 'success' : type === 'error' ? 'error' : type === 'warning' ? 'warning' : 'info');
    showToast(message || title, type);
  };

  const handleSaveService = async (skipDuplicateWarning = false) => {
    // Проверка валидности
    if (!serviceForm.name || serviceForm.name.trim() === '') {
      showAlertModal('Ошибка', 'Введите название услуги', 'error');
      return;
    }

    // Проверка цены при создании
    if (!editingItem && (!serviceForm.base_price || parseFloat(serviceForm.base_price) <= 0)) {
      showAlertModal('Ошибка', 'Введите цену больше 0', 'error');
      return;
    }

    if (!editingItem && serviceForm.unit_type === 'percent' && !isExtraServiceType(serviceForm.service_type)) {
      showAlertModal('Ошибка', 'Процентная цена доступна только для доп. услуг', 'error');
      return;
    }

    if (!editingItem && serviceForm.unit_type === 'percent' && parseFloat(serviceForm.base_price) > 100) {
      showAlertModal('Ошибка', 'Процентная цена не может быть больше 100%', 'error');
      return;
    }

    const duplicateService = findServiceDuplicate();
    if (duplicateService && !skipDuplicateWarning) {
      showConfirmModal(
        'Похожая услуга уже есть',
        `Услуга "${duplicateService.name}" уже есть в этом типе. Сохранить ещё одну запись с таким же названием?`,
        () => handleSaveService(true),
        'warning',
        'Все равно сохранить'
      );
      return;
    }

    const normalizedServiceForm = {
      ...serviceForm,
      unit: isOrderExtraServiceType(serviceForm.service_type) ? ORDER_EXTRA_SERVICE_UNIT : serviceForm.unit
    };

    try {
      if (editingItem) {
        const updatePayload = editingItem.system_code
          ? { execution_credit_stage: normalizedServiceForm.execution_credit_stage }
          : normalizedServiceForm;
        const result = await servicesApi.updateService(editingItem.id, updatePayload);
        console.log('DEBUG updateService result:', result);
        closeServiceModal();
        // Перезагружаем данные чтобы обновить список
        await loadData();
        console.log('DEBUG loadData completed, services:', services);
        showAlertModal('Успешно', 'Услуга обновлена', 'success');
      } else {
        // Показываем подтверждение перед созданием
        showConfirmModal(
          'Создание услуги',
          `Вы уверены, что хотите создать услугу "${normalizedServiceForm.name}"?`,
          async () => {
            try {
              await servicesApi.createService(normalizedServiceForm);
              closeServiceModal();
              loadData();
              showAlertModal('Успешно', 'Услуга создана', 'success');
            } catch (err) {
              showAlertModal('Ошибка', err.message, 'error');
            }
          },
          'info',
          'Создать'
        );
        return; // Выходим, чтобы дождаться подтверждения
      }
      closeServiceModal();
      loadData();
    } catch (err) {
      showAlertModal('Ошибка', err.message, 'error');
    }
  };

  const handleSaveGroup = async (skipDuplicateWarning = false) => {
    if (!groupForm.name || groupForm.name.trim() === '') {
      showAlertModal('Ошибка', 'Введите название группы', 'error');
      return;
    }

    const duplicateGroup = findGroupDuplicate();
    if (duplicateGroup && !skipDuplicateWarning) {
      showConfirmModal(
        'Похожая группа уже есть',
        `Группа "${duplicateGroup.name}" уже есть в этом типе. Сохранить ещё одну запись с таким же названием?`,
        () => handleSaveGroup(true),
        'warning',
        'Все равно сохранить'
      );
      return;
    }

    try {
      if (editingItem) {
        await servicesApi.updateServiceGroup(editingItem.id, groupForm);
        showAlertModal('Успешно', 'Группа обновлена', 'success');
      } else {
        showConfirmModal(
          'Создание группы',
          `Вы уверены, что хотите создать группу "${groupForm.name}"?`,
          async () => {
            try {
              console.log('createServiceGroup:', groupForm);
              await servicesApi.createServiceGroup(groupForm);
              closeGroupModal();
              loadData();
              showAlertModal('Успешно', 'Группа создана', 'success');
            } catch (err) {
              showAlertModal('Ошибка', err.message, 'error');
            }
          },
          'info',
          'Создать'
        );
        return;
      }
      closeGroupModal();
      loadData();
    } catch (err) {
      showAlertModal('Ошибка', err.message, 'error');
    }
  };

  const handleSavePrice = async () => {
    // Проверка даты
    if (!priceForm.effective_from) {
      showAlertModal('Ошибка', 'Выберите дату начала действия цены (не ранее завтра)', 'error');
      return;
    }

    if (!priceForm.price || parseFloat(priceForm.price) <= 0) {
      showAlertModal('Ошибка', 'Введите корректную цену больше 0', 'error');
      return;
    }

    const selectedDate = new Date(`${priceForm.effective_from}T00:00:00`);
    const tomorrow = new Date();
    tomorrow.setHours(0, 0, 0, 0);
    tomorrow.setDate(tomorrow.getDate() + 1);
    if (selectedDate < tomorrow) {
      showAlertModal('Ошибка', 'Изменение цены можно запланировать только начиная с завтрашнего дня', 'error');
      return;
    }

    const priceValue = parseFloat(priceForm.price);

    if (priceForm.unit_type === 'percent' && !isExtraServiceType(selectedService?.service_type)) {
      showAlertModal('Ошибка', 'Процентная цена доступна только для доп. услуг', 'error');
      return;
    }

    if (priceForm.unit_type === 'percent' && priceValue > 100) {
      showAlertModal('Ошибка', 'Процентная цена не может быть больше 100%', 'error');
      return;
    }

    // Формируем текст подтверждения
    const dateStr = new Date(priceForm.effective_from).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
    const confirmMessage = `Вы уверены, что хотите установить цену ${formatServicePrice(priceValue, priceForm.unit_type)} с ${dateStr}?`;

    // Показываем подтверждение
    showConfirmModal(
      'Подтверждение изменения цены',
      confirmMessage,
      async () => {
        await savePrice(priceValue);
      },
      'info',
      'Да'
    );
  };

  const savePrice = async (priceValue, replaceScheduled = false) => {
    try {
      // Формируем данные для отправки
      const priceData = {
        client_type: clientType,
        price: priceValue,
        unit_type: priceForm.unit_type,
        replace_scheduled: replaceScheduled
      };

      // Добавляем дату только если она указана
      if (priceForm.effective_from) {
        priceData.effective_from = priceForm.effective_from;
      }

      console.log('DEBUG createServicePrice:', { serviceId: selectedService.id, priceData });

      await servicesApi.createServicePrice(selectedService.id, priceData);
      closePriceModal();
      loadData();
      showAlertModal('Успешно', 'Цена обновлена', 'success');
    } catch (err) {
      console.error('DEBUG createServicePrice error:', err);
      console.error('DEBUG error details:', JSON.stringify(err, null, 2));
      if (err?.detail?.code === 'scheduled_price_exists') {
        const scheduled = err.detail.scheduled_change;
        const scheduledDate = scheduled?.effective_from
          ? new Date(scheduled.effective_from).toLocaleDateString('ru-RU')
          : 'выбранную дату';
        const scheduledValue = scheduled
          ? formatServicePrice(scheduled.price, scheduled.unit_type)
          : 'запланированное изменение';
        showConfirmModal(
          'Запланированное изменение уже есть',
          `Сейчас уже запланировано ${scheduledValue} с ${scheduledDate}. Перезаписать это изменение новым значением?`,
          async () => {
            await savePrice(priceValue, true);
          },
          'warning',
          'Перезаписать'
        );
        return;
      }
      showAlertModal('Ошибка', err.detail || err.message || 'Неизвестная ошибка', 'error');
    }
  };

  const handleCancelScheduledPrice = (scheduledChange) => {
    if (!selectedService || !scheduledChange) return;

    const scheduledDate = scheduledChange.effective_from
      ? new Date(scheduledChange.effective_from).toLocaleDateString('ru-RU')
      : 'выбранную дату';
    const scheduledValue = formatServicePrice(scheduledChange.price, scheduledChange.unit_type);

    showConfirmModal(
      'Отмена изменения цены',
      `Отменить запланированное изменение ${scheduledValue} с ${scheduledDate}? Текущая цена продолжит действовать дальше.`,
      async () => {
        try {
          setCancelingScheduledPrice(true);
          await servicesApi.cancelScheduledServicePrice(selectedService.id, clientType);
          const priceHistoryData = await servicesApi.getServicePrices(selectedService.id, clientType);
          setPriceHistory(priceHistoryData);
          setPriceForm({
            price: selectedService.current_price ?? selectedService.base_price ?? '',
            unit_type: isExtraServiceType(selectedService.service_type) ? (selectedService.current_unit_type || 'rub') : 'rub',
            effective_from: tomorrowDateString(),
            comment: ''
          });
          await loadData();
          showAlertModal('Успешно', 'Запланированное изменение цены отменено', 'success');
        } catch (err) {
          showAlertModal('Ошибка', err.detail || err.message || 'Не удалось отменить изменение цены', 'error');
        } finally {
          setCancelingScheduledPrice(false);
        }
      },
      'warning',
      'Отменить'
    );
  };

  const handleDeleteService = async (serviceId) => {
    showConfirmModal(
      'Деактивация услуги',
      'Вы уверены, что хотите деактивировать услугу? Это действие нельзя будет отменить.',
      async () => {
        try {
          await servicesApi.deleteService(serviceId);
          loadData();
          showAlertModal('Успешно', 'Услуга деактивирована', 'success');
        } catch (err) {
          showAlertModal('Ошибка', err.message, 'error');
        }
      },
      'danger',
      'Деактивировать'
    );
  };

  const handleDeleteGroup = async (groupId) => {
    try {
      // Сначала получаем информацию о группе с услугами
      const groupData = await servicesApi.getServiceGroup(groupId);
      const servicesCount = groupData.services_count || 0;

      if (servicesCount > 0) {
        // Если есть услуги, получаем их список
        const servicesInGroup = await servicesApi.getServices({
          group_id: groupId,
          active_only: true
        });

        const servicesList = servicesInGroup.map(s => ({ id: s.id, name: s.name }));

        showConfirmModal(
          'Деактивация группы',
          `В этой группе находится ${servicesCount} усл.(и). После деактивации группы эти услуги перейдут в разряд "Без группы".`,
          async () => {
            try {
              await servicesApi.deleteServiceGroup(groupId);
              loadData();
              showAlertModal('Успешно', 'Группа деактивирована', 'success');
            } catch (err) {
              showAlertModal('Ошибка', err.message, 'error');
            }
          },
          'warning',
          'Деактивировать',
          servicesList
        );
      } else {
        // Если услуг нет, показываем простое подтверждение
        showConfirmModal(
          'Деактивация группы',
          'Вы уверены, что хотите деактивировать группу? Это действие нельзя будет отменить.',
          async () => {
            try {
              await servicesApi.deleteServiceGroup(groupId);
              loadData();
              showAlertModal('Успешно', 'Группа деактивирована', 'success');
            } catch (err) {
              showAlertModal('Ошибка', err.message, 'error');
            }
          },
          'danger',
          'Деактивировать'
        );
      }
    } catch (err) {
      showAlertModal('Ошибка', err.message, 'error');
    }
  };

  const getGroupFilterOptions = () => {
    if (selectedType === 'all') return [];
    return groups.filter(g => g.service_type === selectedType);
  };

  // Получение групп для формы (фильтр по типу услуги в форме)
  const getFormGroups = () => {
    if (!serviceForm.service_type) return [];
    return groups.filter(g => g.service_type === serviceForm.service_type);
  };

  return (
    <div className="space-y-3 animate-fade-in desktop-content-container-wide">
      {/* Переключатель B2C/B2B - первый ряд */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-3 shadow-sm animate-slide-up" style={{ animationDelay: '0.12s' }}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Тип клиента</span>
          <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
        </div>
        <ManagementSegmentedControl
          value={clientType}
          onChange={setClientType}
          compact
          options={[
            { value: 'b2c', label: 'Физические лица', icon: Users },
            { value: 'b2b', label: 'Организации', icon: BriefcaseBusiness }
          ]}
        />
      </div>

      {/* Вкладки Услуги/Группы - второй ряд */}
      <div className="bg-white dark:bg-gray-900 rounded-xl p-3 shadow-sm animate-slide-up" style={{ animationDelay: '0.15s' }}>
        <div className="flex items-center justify-center gap-2 mb-2">
          <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
          <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Раздел</span>
          <div className="h-px bg-gray-200 dark:bg-gray-700 flex-1"></div>
        </div>
        <ManagementSegmentedControl
          value={activeTab}
          onChange={setActiveTab}
          options={[
            { value: 'services', label: 'Услуги', icon: Sparkles },
            { value: 'groups', label: 'Группы', icon: Layers }
          ]}
        />
      </div>

      {/* Панель инструментов */}
      <div className="relative z-20 bg-white dark:bg-gray-900 rounded-xl p-3 shadow-sm animate-slide-up" style={{ animationDelay: '0.2s' }}>
        <div className="flex flex-col gap-3">
          {/* Поиск */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Поиск..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Второй ряд: фильтры */}
          <div className="flex flex-wrap items-center gap-2">
            {/* Фильтр по типу */}
            <div className="flex-1 min-w-[220px]">
              <Dropdown
                label="Тип услуги"
                value={selectedType}
                options={SERVICE_TYPE_FILTER_OPTIONS.filter((option) => (
                  option.value === 'all' || serviceTypes.some((type) => type.value === option.value)
                ))}
                onChange={setSelectedType}
                placeholder="Все типы"
              />
            </div>

            {/* Показать неактивные */}
            <button
              onClick={() => setShowInactive(!showInactive)}
              className={`px-3 py-2 rounded-xl font-medium transition-all flex items-center text-sm whitespace-nowrap ${showInactive
                ? 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400'
                : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                }`}
            >
              {showInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              <span className="hidden sm:inline ml-1">{showInactive ? 'Неактивные' : 'Активные'}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Основной контент */}
      {loading ? (
        <ServicesListSkeleton activeTab={activeTab} />
      ) : error ? (
        <div className="bg-white dark:bg-gray-900 rounded-2xl p-6 animate-slide-up text-center">
          <p className="text-gray-500 dark:text-gray-400">Не удалось загрузить данные. Попробуй обновить страницу.</p>
        </div>
      ) : activeTab === 'services' ? (
        /* Список услуг */
        <div className="relative z-0 space-y-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          {/* Заголовок таблицы с кнопкой добавления */}
          <div className="hidden sm:flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex-1">
              <div className="col-span-3">Название</div>
              <div className="col-span-2">Тип</div>
              <div className="col-span-2">Группа</div>
              <div className="col-span-1">Ед.</div>
              <div className="col-span-2">Цена</div>
              <div className="col-span-1">Статус</div>
              <div className="col-span-1 text-right">Действия</div>
            </div>
            <div className="ml-4 flex items-center gap-2">
              <div className="w-[180px]">
                <Dropdown
                  value={serviceSort}
                  options={SERVICE_SORT_OPTIONS}
                  onChange={setServiceSort}
                  placeholder="Сортировка"
                  buttonClassName="!h-9 !min-h-9"
                />
              </div>
              <button
                onClick={() => handleOpenServiceModal()}
                className="w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg hover:scale-105"
                title="Добавить услугу"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Заголовок для мобильных */}
          <div className="sm:hidden flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {filteredServices.length} {filteredServices.length === 1 ? 'услуга' : filteredServices.length < 5 ? 'услуги' : 'услуг'}
            </span>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  const currentIndex = SERVICE_SORT_OPTIONS.findIndex((option) => option.value === serviceSort);
                  const nextOption = SERVICE_SORT_OPTIONS[(currentIndex + 1) % SERVICE_SORT_OPTIONS.length];
                  setServiceSort(nextOption.value);
                }}
                className="inline-flex h-9 items-center gap-1 rounded-xl border border-gray-200 bg-white px-2.5 text-xs font-medium text-gray-700 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                title="Сменить сортировку"
              >
                <ArrowDownUp className="h-4 w-4" />
                <span>{SERVICE_SORT_OPTIONS.find((option) => option.value === serviceSort)?.label || 'Сортировка'}</span>
              </button>
              <button
                onClick={() => handleOpenServiceModal()}
                className="w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg hover:scale-105"
                title="Добавить услугу"
              >
                <Plus className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Карточки услуг */}
          {filteredServices.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery || selectedType !== 'all' ? 'Ничего не найдено' : 'Услуги ещё не созданы'}
              </p>
            </div>
          ) : (
            sortedServices.map(service => {
              const iconColor = service.client_type === 'b2b' ? 'text-purple-600 dark:text-purple-400' : 'text-blue-600 dark:text-blue-400';
              const bgColor = service.client_type === 'b2b' ? 'bg-purple-100 dark:bg-purple-900/20' : 'bg-blue-100 dark:bg-blue-900/20';
              const IconComp = getServiceIconComponent(service.icon);

              return (
                <div key={service.id} className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                  {/* Верхняя строка: название и цены */}
                  <div className="flex items-start justify-between mb-2 gap-2">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      {/* Иконка услуги */}
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${bgColor}`}>
                        <IconComp className={`w-6 h-6 ${iconColor}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{service.name}</div>
                          {service.system_code && (
                            <span className="shrink-0 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-amber-700 dark:bg-amber-900/30 dark:text-amber-200">
                              системная
                            </span>
                          )}
                        </div>
                        {service.description && (
                          <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 sm:hidden">{service.description}</div>
                        )}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-bold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {formatServicePrice(service.current_price ?? service.base_price, service.current_unit_type)}
                      </div>
                      {service.next_scheduled_price !== null && service.next_scheduled_price !== undefined && service.next_scheduled_effective_from && (
                        <div className="mt-0.5 text-[11px] font-medium leading-4 text-amber-700 dark:text-amber-300">
                          с {formatServiceDate(service.next_scheduled_effective_from)}: {formatServicePrice(service.next_scheduled_price, service.next_scheduled_unit_type)}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Средняя строка: тип и группа */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 whitespace-nowrap">
                      {SERVICE_TYPE_LABELS[service.service_type]}
                    </span>
                    {service.group && (
                      <span className="text-xs text-gray-600 dark:text-gray-400 truncate">
                        {service.group.name}
                      </span>
                    )}
                    {isExtraServiceType(service.service_type) && (
                      <span className="truncate text-xs text-gray-500 dark:text-gray-400">
                        Выполнение: {EXECUTION_CREDIT_LABELS[service.execution_credit_stage] || 'Не настроено'}
                      </span>
                    )}
                  </div>

                  {/* Нижняя строка: ед. измерения, статус и действия */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {UNIT_LABELS[service.unit] || service.unit}
                      </span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${service.active
                        ? 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400'
                        : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400'
                        }`}>
                        {service.active ? 'Активна' : 'Неакт.'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleOpenAuditModal(service)}
                        className="p-1.5 text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-900/20 rounded-lg transition-colors"
                        title="История"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleOpenPriceModal(service)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title="Изменить цену"
                      >
                        <TrendingUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditServiceClick(service)}
                        className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                        title={service.system_code && isExtraServiceType(service.service_type) ? 'Настроить выполнение' : service.system_code ? 'Редактирование системной услуги недоступно' : 'Редактировать'}
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      {!service.system_code && (
                        <button
                          onClick={() => handleDeleteService(service.id)}
                          className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Деактивировать"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : (
        /* Список групп */
        <div className="relative z-0 space-y-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
          {/* Заголовок таблицы с кнопкой добавления */}
          <div className="hidden sm:flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl px-4 py-3">
            <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider flex-1">
              <div className="col-span-3">Название</div>
              <div className="col-span-2">Тип</div>
              <div className="col-span-2">Выбор</div>
              <div className="col-span-1">Услуг</div>
              <div className="col-span-2">Статус</div>
              <div className="col-span-2 text-right">Действия</div>
            </div>
            <button
              onClick={() => handleOpenGroupModal()}
              className="ml-4 w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg hover:scale-105"
              title="Добавить группу"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Заголовок для мобильных */}
          <div className="sm:hidden flex items-center justify-between bg-gray-50 dark:bg-gray-800 rounded-xl px-3 py-2">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
              {filteredGroups.length} {filteredGroups.length === 1 ? 'группа' : filteredGroups.length < 5 ? 'группы' : 'групп'}
            </span>
            <button
              onClick={() => handleOpenGroupModal()}
              className="w-9 h-9 flex items-center justify-center bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors shadow-md hover:shadow-lg hover:scale-105"
              title="Добавить группу"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Карточки групп */}
          {filteredGroups.length === 0 ? (
            <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 text-center">
              <p className="text-gray-500 dark:text-gray-400">
                {searchQuery || selectedType !== 'all' ? 'Ничего не найдено' : 'Группы ещё не созданы'}
              </p>
            </div>
          ) : (
            filteredGroups.map(group => (
              <div key={group.id} className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors">
                {/* Верхняя строка: название и кол-во услуг */}
                <div className="flex items-center justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{group.name}</div>
                    {group.description && (
                      <div className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5 sm:hidden">{group.description}</div>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 ml-2 whitespace-nowrap">
                    {group.services_count || 0} усл.
                  </div>
                </div>

                {/* Средняя строка: тип и выбор */}
                <div className="flex items-center gap-2 mb-2">
                  <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 whitespace-nowrap">
                    {SERVICE_TYPE_LABELS[group.service_type]}
                  </span>
                  <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${group.allow_multiple
                    ? 'bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400'
                    : 'bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400'
                    }`}>
                    {group.allow_multiple ? 'Неск.' : 'Одна'}
                  </span>
                </div>

                {/* Нижняя строка: действия */}
                <div className="flex items-center justify-between">
                  <div />
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => handleOpenGroupModal(group)}
                      className="p-1.5 text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                      title="Редактировать"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteGroup(group.id)}
                      className="p-1.5 text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                      title="Деактивировать"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
            {/* Модальное окно услуги - компактное для телефона */}
      <ModalShell
        open={showServiceModal}
        onClose={closeServiceModal}
        variant="sheet"
        zIndexClass="z-[70]"
        panelClassName="max-h-[90vh] overflow-y-auto sm:max-w-lg"
      >
            <div className="sticky top-0 bg-white dark:bg-gray-900 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {editingItem ? 'Редактировать услугу' : 'Новая услуга'}
              </h2>
              <button onClick={closeServiceModal} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {editingItem?.system_code && (
                <div className="rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950/30 dark:text-amber-200">
                  У системной услуги можно изменить только настройку выполнения.
                </div>
              )}
              {/* Название */}
              <div>
                <FloatingField label="Название *" disabled={Boolean(editingItem?.system_code)}>
                  <input
                    type="text"
                    value={serviceForm.name}
                    onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                    disabled={Boolean(editingItem?.system_code)}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                    placeholder="Например: Рубашка"
                  />
                </FloatingField>
                {findServiceDuplicate() && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Уже есть услуга с таким названием. Сохранить все равно можно.
                  </p>
                )}
              </div>

              {/* Иконка */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Иконка</label>
                <button
                  type="button"
                  onClick={() => { setIconPickerType('service'); setShowIconPicker(true); }}
                  disabled={Boolean(editingItem?.system_code)}
                  className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl flex items-center justify-between bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300">
                      {(() => {
                        const SelectedIcon = getServiceIconComponent(serviceForm.icon);
                        return <SelectedIcon className="h-5 w-5" />;
                      })()}
                    </div>
                    <div className="min-w-0 text-left text-sm font-medium text-gray-700 dark:text-gray-300">
                      {serviceForm.icon ? 'Иконка выбрана' : 'Без иконки'}
                    </div>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">Изменить</span>
                </button>
              </div>

              {/* Тип услуги */}
              <div>
                <Dropdown
                  label="Тип услуги *"
                  value={serviceForm.service_type}
                  options={serviceTypes.map(t => ({ value: t.value, label: getServiceTypeLabel(t.value, t.label) }))}
                  onChange={(value) => setServiceForm({
                    ...serviceForm,
                    service_type: value,
                    processing_route: value === 'washing' ? (serviceForm.processing_route || 'laundry') : null,
                    execution_credit_stage: getDefaultExecutionCreditStage(value),
                    group_id: null,
                    unit: isOrderExtraServiceType(value) ? ORDER_EXTRA_SERVICE_UNIT : serviceForm.unit,
                    unit_type: isExtraServiceType(value) ? serviceForm.unit_type : 'rub'
                  })}
                  placeholder="Выберите тип"
                  disabled={Boolean(editingItem?.system_code)}
                />
              </div>

              {serviceForm.service_type === 'washing' && (
                <div>
                  <Dropdown
                    label="Поток стирки"
                    value={serviceForm.processing_route}
                    options={PROCESSING_ROUTE_OPTIONS.map((option) => ({
                      ...option,
                      renderLabel: <ProcessingRouteOptionLabel value={option.value} />
                    }))}
                    onChange={(value) => setServiceForm({ ...serviceForm, processing_route: value })}
                    placeholder="Выберите поток"
                  />
                </div>
              )}

              {isExtraServiceType(serviceForm.service_type) && (
                <div>
                  <Dropdown
                    label="Кому засчитывать выполнение"
                    value={serviceForm.execution_credit_stage || getDefaultExecutionCreditStage(serviceForm.service_type, editingItem?.system_code)}
                    options={getExecutionCreditOptions(serviceForm.service_type)}
                    onChange={(value) => setServiceForm({ ...serviceForm, execution_credit_stage: value })}
                    placeholder="Выберите этап"
                  />
                  <p className="mt-1 text-xs leading-5 text-gray-500 dark:text-gray-400">
                    Выполнение получит сотрудник, завершивший выбранный этап или предмет в выбранном потоке.
                  </p>
                </div>
              )}

              {/* Группа */}
              <div>
                <Dropdown
                  label="Группа"
                  value={serviceForm.group_id || ''}
                  options={[{ value: '', label: 'Без группы' }, ...getFormGroups().map(g => ({ value: g.id, label: g.name }))]}
                  onChange={(value) => setServiceForm({ ...serviceForm, group_id: value ? parseInt(value) : null })}
                  placeholder="Выберите группу"
                  disabled={!serviceForm.service_type || Boolean(editingItem?.system_code)}
                />
                {!serviceForm.service_type && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Сначала выберите тип услуги
                  </p>
                )}
              </div>

              {/* Ед. измерения и текущая цена */}
              <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3">
                <div className="min-w-0">
                  <Dropdown
                    label="Ед. измерения"
                    value={serviceForm.unit}
                    options={(isOrderExtraServiceType(serviceForm.service_type) ? [{ value: ORDER_EXTRA_SERVICE_UNIT, label: UNIT_LABELS[ORDER_EXTRA_SERVICE_UNIT] || ORDER_EXTRA_SERVICE_UNIT }] : units).map(u => ({ value: u.value, label: UNIT_LABELS[u.value] || u.value }))}
                    onChange={(value) => setServiceForm({ ...serviceForm, unit: value })}
                    placeholder="Выберите"
                    buttonClassName="h-[44px] min-h-[44px]"
                    disabled={isOrderExtraServiceType(serviceForm.service_type) || Boolean(editingItem?.system_code)}
                  />
                  {isOrderExtraServiceType(serviceForm.service_type) && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Для доп. услуг заказа доступна только единица `шт.`
                    </p>
                  )}
                </div>
                {!editingItem && (
                  <div className="min-w-0">
                    <div className="flex gap-2 items-center">
                      <FloatingField label={`Цена ${serviceForm.unit_type === 'percent' ? '(%)' : '(₽)'}`} className="min-w-0 flex-1">
                        <input
                          type="number"
                          value={serviceForm.base_price || ''}
                          onChange={(e) => {
                            const val = e.target.value === '' ? '' : parseFloat(e.target.value) || 0;
                            setServiceForm({ ...serviceForm, base_price: val });
                          }}
                          className="h-[44px] min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                          placeholder="0"
                          min="0.01"
                          step="0.01"
                        />
                      </FloatingField>
                      {isExtraServiceType(serviceForm.service_type) ? (
                        <div className="flex h-[44px] min-h-[44px] rounded-xl overflow-hidden border border-gray-300 dark:border-gray-600 shrink-0">
                          <button
                            type="button"
                            onClick={() => setServiceForm({ ...serviceForm, unit_type: 'rub' })}
                            className={`px-4 py-2.5 text-sm font-semibold transition-all ${serviceForm.unit_type === 'rub'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}
                          >
                            ₽
                          </button>
                          <button
                            type="button"
                            onClick={() => setServiceForm({ ...serviceForm, unit_type: 'percent' })}
                            className={`px-4 py-2.5 text-sm font-semibold transition-all border-l border-gray-300 dark:border-gray-600 ${serviceForm.unit_type === 'percent'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}
                          >
                            %
                          </button>
                        </div>
                      ) : (
                        <div className="h-[44px] min-h-[44px] min-w-[54px] px-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-sm font-semibold text-gray-600 dark:text-gray-300 shrink-0">
                          ₽
                        </div>
                      )}
                    </div>
                    {!isExtraServiceType(serviceForm.service_type) && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Процентная цена доступна только для доп. услуг
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Описание */}
              <div>
                <FloatingField label="Описание" disabled={Boolean(editingItem?.system_code)}>
                  <textarea
                    value={serviceForm.description}
                    onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                    disabled={Boolean(editingItem?.system_code)}
                    className="w-full resize-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                    rows="2"
                    placeholder="Описание услуги..."
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </FloatingField>
                <div className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
                  {(serviceForm.description || '').length}/{DESCRIPTION_MAX_LENGTH}
                </div>
              </div>

              {/* Кнопки */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={closeServiceModal}
                  className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
                >
                  Отмена
                </button>
                <button
                  onClick={handleSaveService}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm flex items-center justify-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Сохранить
                </button>
              </div>
            </div>
      </ModalShell>

      {/* Модальное окно группы - компактное для телефона */}
      <ModalShell
        open={showGroupModal}
        onClose={closeGroupModal}
        variant="sheet"
        zIndexClass="z-[70]"
        panelClassName="max-h-[90vh] overflow-y-auto sm:max-w-md"
      >
            <div className="sticky top-0 bg-white dark:bg-gray-900 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
                {editingItem ? 'Редактировать группу' : 'Новая группа'}
              </h2>
              <button onClick={closeGroupModal} className="text-gray-400 hover:text-gray-600 p-1">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-3">
              {/* Название */}
              <div>
                <FloatingField label="Название *">
                  <input
                    type="text"
                    value={groupForm.name}
                    onChange={(e) => setGroupForm({ ...groupForm, name: e.target.value })}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                    placeholder="Например: Верхняя одежда"
                  />
                </FloatingField>
                {findGroupDuplicate() && (
                  <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
                    Уже есть группа с таким названием. Сохранить все равно можно.
                  </p>
                )}
              </div>

              {/* Тип услуг */}
              <div>
                <Dropdown
                  label="Тип услуг *"
                  value={groupForm.service_type}
                  options={serviceTypes.map(t => ({ value: t.value, label: getServiceTypeLabel(t.value, t.label) }))}
                  onChange={(value) => setGroupForm({ ...groupForm, service_type: value })}
                  placeholder="Выберите тип"
                />
              </div>

              {/* Выбор услуг */}
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Выбор услуг</label>
                <ManagementSegmentedControl
                  value={groupForm.allow_multiple ? 'multiple' : 'single'}
                  onChange={(value) => setGroupForm({ ...groupForm, allow_multiple: value === 'multiple' })}
                  compact
                  columns={2}
                  options={[
                    { value: 'single', label: 'Одна' },
                    { value: 'multiple', label: 'Несколько' }
                  ]}
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {groupForm.allow_multiple
                    ? 'Пользователи смогут выбирать несколько услуг из этой группы'
                    : 'В этой группе можно выбрать только одну услугу'}
                </p>
              </div>

              {/* Описание */}
              <div>
                <FloatingField label="Описание">
                  <textarea
                    value={groupForm.description}
                    onChange={(e) => setGroupForm({ ...groupForm, description: e.target.value })}
                    className="w-full resize-none px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                    rows="2"
                    placeholder="Описание группы..."
                    maxLength={DESCRIPTION_MAX_LENGTH}
                  />
                </FloatingField>
                <div className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500">
                  {(groupForm.description || '').length}/{DESCRIPTION_MAX_LENGTH}
                </div>
              </div>

              {/* Кнопки */}
              <div className="flex gap-2 pt-2">
                <button
                  onClick={closeGroupModal}
                  className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
                >
                  Отмена
                </button>
                <button
                  onClick={handleSaveGroup}
                  className="flex-1 px-4 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium text-sm flex items-center justify-center"
                >
                  <Save className="w-4 h-4 mr-2" />
                  Сохранить
                </button>
              </div>
            </div>
      </ModalShell>

      {/* Модальное окно цены - с историей и календарём */}
      <ModalShell
        open={showPriceModal && Boolean(selectedService)}
        onClose={closePriceModal}
        variant="sheet"
        zIndexClass="z-[70]"
        panelClassName="max-h-[95vh] overflow-y-auto sm:max-w-lg"
      >
            {/* Заголовок */}
            <div className="sticky top-0 bg-white dark:bg-gray-900 p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between z-10">
              <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">Изменение цены</h2>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setShowHistory(!showHistory)}
                  className="p-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg"
                  title="История цен"
                >
                  <History className="w-5 h-5" />
                </button>
                <button onClick={closePriceModal} className="text-gray-400 hover:text-gray-600 p-1">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

              <div className="p-4 space-y-3">
                {(() => {
                  const scheduledChange = getScheduledPriceChange(priceHistory);
                  return scheduledChange ? (
                  <div className="rounded-xl border border-amber-200 bg-amber-50/80 p-3 dark:border-amber-900/60 dark:bg-amber-950/20">
                    <div className="flex flex-col gap-2">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 text-sm font-semibold text-amber-800 dark:text-amber-200">
                          <Clock className="w-4 h-4 shrink-0" />
                          <span>Запланированное изменение</span>
                        </div>
                        <div className="mt-1 text-xs leading-5 text-amber-700 dark:text-amber-300">
                          {formatServicePrice(scheduledChange.price, scheduledChange.unit_type)} с {new Date(scheduledChange.effective_from).toLocaleDateString('ru-RU')}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleCancelScheduledPrice(scheduledChange)}
                        disabled={cancelingScheduledPrice}
                        className={`inline-flex min-h-[38px] w-full items-center justify-center gap-2 rounded-lg border px-3 text-sm font-semibold transition-colors ${
                          cancelingScheduledPrice
                            ? 'border-amber-200 bg-amber-100 text-amber-400 cursor-not-allowed dark:border-amber-900/60 dark:bg-amber-950/40 dark:text-amber-700'
                            : 'border-amber-300 bg-white text-amber-800 shadow-sm hover:bg-amber-100 dark:border-amber-800 dark:bg-gray-900 dark:text-amber-200 dark:hover:bg-amber-900/30'
                        }`}
                      >
                        <X className="h-4 w-4" />
                        <span>{cancelingScheduledPrice ? 'Отменяем...' : 'Отменить изменение цены'}</span>
                      </button>
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Информация об услуге */}
              <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-xl">
                <p className="text-xs text-gray-600 dark:text-gray-400">Услуга:</p>
                <p className="font-medium text-gray-900 dark:text-gray-100 truncate text-sm">
                  {selectedService?.name || 'Услуга не выбрана'}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-2">Текущая цена:</p>
                <p className="text-xl font-bold text-blue-600">
                  {formatServicePrice(
                    selectedService?.current_price ?? selectedService?.base_price,
                    selectedService?.current_unit_type
                  )}
                </p>
              </div>

              {!showHistory ? (
                <>
                  {/* Цена */}
                  <div>
                    <div className="flex gap-2 items-center">
                      <FloatingField label="Новая цена *" className="min-w-0 flex-1">
                        <input
                          type="number"
                          value={priceForm.price}
                          onChange={(e) => setPriceForm({ ...priceForm, price: e.target.value })}
                          className="h-[44px] min-h-[44px] w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 text-sm"
                          placeholder="0"
                          min="0.01"
                          step="0.01"
                        />
                      </FloatingField>
                      {selectedServiceSupportsPercent ? (
                        <div className="flex h-[44px] min-h-[44px] rounded-xl overflow-hidden border border-gray-300 dark:border-gray-600 shrink-0">
                          <button
                            type="button"
                            onClick={() => setPriceForm({ ...priceForm, unit_type: 'rub' })}
                            className={`px-4 py-2.5 text-sm font-semibold transition-all ${priceForm.unit_type === 'rub'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}
                          >
                            ₽
                          </button>
                          <button
                            type="button"
                            onClick={() => setPriceForm({ ...priceForm, unit_type: 'percent' })}
                            className={`px-4 py-2.5 text-sm font-semibold transition-all border-l border-gray-300 dark:border-gray-600 ${priceForm.unit_type === 'percent'
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                              }`}
                          >
                            %
                          </button>
                        </div>
                      ) : (
                        <div className="h-[44px] min-h-[44px] min-w-[54px] px-3 border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center text-sm font-semibold text-gray-600 dark:text-gray-300 shrink-0">
                          ₽
                        </div>
                      )}
                    </div>
                    {!selectedServiceSupportsPercent && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        Процентная цена доступна только для доп. услуг
                      </p>
                    )}
                    {isPricePercentOverLimit && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400">
                        Процентная цена не может быть больше 100%
                      </p>
                    )}
                  </div>

                  {/* Дата начала действия */}
                  <div>
                    <DatePicker
                      value={priceForm.effective_from}
                      onChange={(date) => setPriceForm({ ...priceForm, effective_from: date })}
                      placeholder="Дата начала действия"
                      allowPastDates={false}
                      showTodayButton={false}
                      minDate={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                      inputClassName="h-[44px] min-h-[44px] px-3 pl-8 pr-8 text-sm placeholder:text-sm text-left"
                    />
                    {!priceForm.effective_from && (
                      <p className="text-xs text-orange-600 dark:text-orange-400 mt-1 font-medium">
                        ⚠️ Дата обязательна для выбора
                      </p>
                    )}
                  </div>

                  {/* Кнопки */}
                  <div className="flex gap-2 pt-2">
                    <button
                      onClick={closePriceModal}
                      className="flex-1 px-4 py-2.5 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors font-medium text-sm"
                    >
                      Отмена
                    </button>
                    <button
                      onClick={handleSavePrice}
                      disabled={isPriceSaveDisabled}
                      className={`flex-1 px-4 py-2.5 rounded-xl font-medium text-sm flex items-center justify-center transition-colors ${isPriceSaveDisabled
                        ? 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-600 cursor-not-allowed'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                        }`}
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Сохранить
                    </button>
                  </div>
                </>
              ) : (
                /* История цен */
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-2">История изменений</h3>
                  {priceHistory.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">История пуста</p>
                  ) : (
                    priceHistory.map((item, idx) => (
                      <div
                        key={item.id}
                        className={`p-3 rounded-xl border ${item.is_active
                          ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                          : 'bg-gray-50 dark:bg-gray-800 border-gray-200 dark:border-gray-700'
                          }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {item.price} {item.unit_type === 'rub' ? '₽' : '%'}
                          </span>
                          {item.is_active && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-600 text-white">
                              Действует
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            с {new Date(item.effective_from).toLocaleDateString('ru-RU')}
                          </span>
                          {item.effective_to && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              по {new Date(item.effective_to).toLocaleDateString('ru-RU')}
                            </span>
                          )}
                        </div>
                        {item.comment && (
                          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">{item.comment}</p>
                        )}
                        <div className="flex items-center gap-1 mt-2 text-xs text-gray-400 dark:text-gray-500">
                          <User className="w-3 h-3" />
                          <span>{item.creator_name || 'Неизвестно'}</span>
                          <span>•</span>
                          <span>{new Date(item.created_date).toLocaleDateString('ru-RU')}</span>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
      </ModalShell>

      <ServiceActivityModal
        open={showAudit}
        onClose={closeAuditModal}
        service={selectedAuditService}
      />

      {/* Модальные окна подтверждений и алертов */}
      {createPortal(<ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        type={confirmModal.type}
        confirmText={confirmModal.confirmText}
        cancelText={confirmModal.cancelText}
        servicesList={confirmModal.servicesList}
        onConfirm={() => {
          haptic('success');
          confirmModal.onConfirm?.();
          setConfirmModal({ ...confirmModal, isOpen: false, onConfirm: null });
        }}
        onCancel={() => {
          haptic('close');
          setConfirmModal({ ...confirmModal, isOpen: false, onConfirm: null });
        }}
      />, document.body)}

      {/* Модальное окно выбора иконки */}
      <IconPickerModal
        open={showIconPicker}
        onClose={() => setShowIconPicker(false)}
        onSelect={(iconName) => {
          if (iconPickerType === 'service') {
            setServiceForm({ ...serviceForm, icon: iconName });
          } else {
            setGroupForm({ ...groupForm, icon: iconName });
          }
        }}
        selectedIcon={iconPickerType === 'service' ? serviceForm.icon : groupForm.icon}
        iconColor={clientType === 'b2b' ? 'purple' : 'blue'}
        title="Иконка услуги"
        iconOptions={SERVICE_ICON_OPTIONS}
      />
    </div>
  );
};

export default ServicesManagement;
